import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Progress } from "./components/ui/progress";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./components/ui/popover";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import {
  Upload,
  Download,
  Copy,
  Trash2,
  Edit3,
  LogIn,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  File as FileIcon,
  MoreVertical,
  Folder,
  Home,
  Search,
  User,
  Plus,
  Eye,
  PlayCircle,
} from "lucide-react";

//const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
//const API = `${BACKEND_URL}/api`;

const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || window.location.origin
).replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

function App() {
  const [files, setFiles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: "user",
  });
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [uploadData, setUploadData] = useState({ title: "", file: null });
  const [uploadFileInputKey, setUploadFileInputKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [openMenuId, setOpenMenuId] = useState(null);

  // PWA Install Prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    fetchFiles();
    // Check if user is already logged in
    const token = localStorage.getItem("auth_token");
    if (token) {
      fetchCurrentUser();
    }

    // PWA Install Prompt Setup
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install prompt after 30 seconds if not installed
      setTimeout(() => {
        if (!window.matchMedia("(display-mode: standalone)").matches) {
          setShowInstallPrompt(true);
        }
      }, 30000);
    };

    const handleAppInstalled = () => {
      console.log("PWA installed successfully");
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      toast.success("FileShare berhasil diinstal!");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Register Service Worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          console.log("SW registered: ", registration);
        })
        .catch((registrationError) => {
          console.log("SW registration failed: ", registrationError);
        });
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentUser(response.data);
    } catch (error) {
      localStorage.removeItem("auth_token");
      setCurrentUser(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const endpoint = token ? `${API}/files/authenticated` : `${API}/files`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(endpoint, { headers });
      setFiles(response.data);
    } catch (error) {
      console.error("Error fetching files:", error);
      // If authenticated endpoint fails, try public endpoint
      if (localStorage.getItem("auth_token")) {
        try {
          const response = await axios.get(`${API}/files`);
          setFiles(response.data);
        } catch (publicError) {
          toast.error("Failed to load files");
        }
      } else {
        toast.error("Failed to load files");
      }
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const openPreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  // taruh di atas return, bareng helper lain
  const getPreviewIcon = (type = "") =>
    type.startsWith("audio/") || type.startsWith("video/") ? PlayCircle : Eye;

  const isPreviewable = (type) =>
    type.startsWith("image/") ||
    type.startsWith("video/") ||
    type.startsWith("audio/") ||
    type.startsWith("text/") ||
    type === "application/pdf";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("auth_token", response.data.access_token);
      setCurrentUser(response.data.user);
      setShowLogin(false);
      setLoginData({ username: "", password: "" });
      toast.success("Login successful!");
      fetchFiles(); // Refresh files to show permissions
    } catch (error) {
      toast.error("Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    setCurrentUser(null);
    setShowProfile(false);
    toast.success("Logged out successfully");
    fetchFiles(); // Refresh files to update permissions
  };

  const createUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewUser({ username: "", password: "", role: "user" });
      toast.success("User created successfully!");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const compressImage = async (file) => {
    // Only compress images
    if (!file.type.startsWith("image/")) {
      return file;
    }

    // Target maksimal 500KB (0.5MB)
    const targetSizeKB = 500;
    const targetSizeMB = targetSizeKB / 1024;

    let options = {
      maxSizeMB: targetSizeMB,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.8,
      alwaysKeepResolution: false,
    };

    try {
      let compressedFile = await imageCompression(file, options);

      // Jika masih lebih dari 500KB, kompres lebih agresif
      if (compressedFile.size > targetSizeKB * 1024) {
        options.maxSizeMB = targetSizeMB;
        options.maxWidthOrHeight = 1280;
        options.initialQuality = 0.6;
        compressedFile = await imageCompression(file, options);
      }

      // Jika masih lebih dari 500KB, kompres sangat agresif
      if (compressedFile.size > targetSizeKB * 1024) {
        options.maxSizeMB = targetSizeMB;
        options.maxWidthOrHeight = 800;
        options.initialQuality = 0.4;
        compressedFile = await imageCompression(file, options);
      }

      // Jika masih lebih dari 500KB, kompres ekstrem
      if (compressedFile.size > targetSizeKB * 1024) {
        options.maxSizeMB = targetSizeMB;
        options.maxWidthOrHeight = 600;
        options.initialQuality = 0.2;
        compressedFile = await imageCompression(file, options);
      }

      const originalSizeKB = Math.round(file.size / 1024);
      const compressedSizeKB = Math.round(compressedFile.size / 1024);

      console.log(`Original size: ${originalSizeKB} KB`);
      console.log(`Compressed size: ${compressedSizeKB} KB`);

      if (compressedFile.size < file.size) {
        const compressionRatio = Math.round(
          ((file.size - compressedFile.size) / file.size) * 100
        );
        toast.success(
          `Image compressed: ${originalSizeKB}KB → ${compressedSizeKB}KB (${compressionRatio}% smaller)`
        );
        return compressedFile;
      }

      toast.info(`Image size: ${originalSizeKB}KB (no compression needed)`);
      return file;
    } catch (error) {
      console.error("Compression failed:", error);
      toast.warning("Image compression failed, uploading original file");
      return file;
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.title) {
      toast.error("Please select a file and enter a title");
      return;
    }

    setLoading(true);

    try {
      // Compress image if needed
      const fileToUpload = await compressImage(uploadData.file);

      const formData = new FormData();
      formData.append("file", fileToUpload);

      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/files/upload?title=${encodeURIComponent(uploadData.title)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("File uploaded successfully!");
      setUploadData({ title: "", file: null });
      setUploadFileInputKey((prev) => prev + 1); // Reset file input
      setShowUpload(false);
      fetchFiles();
    } catch (error) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("File deleted successfully");
      fetchFiles();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleEditTitle = async (fileId) => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${API}/files/${fileId}/edit`,
        { title: editTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Title updated successfully");
      setEditingFile(null);
      setEditTitle("");
      fetchFiles();
    } catch (error) {
      toast.error("Failed to update title");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Direct link copied to clipboard!");
  };

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("User accepted the install prompt");
      toast.success("Aplikasi sedang diinstal...");
    } else {
      console.log("User dismissed the install prompt");
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    localStorage.setItem("install-prompt-dismissed", Date.now().toString());
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) return <ImageIcon className="h-5 w-5" />;
    if (fileType.startsWith("video/")) return <Video className="h-5 w-5" />;
    if (fileType.startsWith("audio/")) return <Music className="h-5 w-5" />;
    return <FileIcon className="h-5 w-5" />;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "documents":
        return <FileText className="h-6 w-6" />;
      case "videos":
        return <Video className="h-6 w-6" />;
      case "images":
        return <ImageIcon className="h-6 w-6" />;
      case "music":
        return <Music className="h-6 w-6" />;
      default:
        return <Folder className="h-6 w-6" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "documents":
        return "bg-red-100 text-red-600";
      case "videos":
        return "bg-yellow-100 text-yellow-600";
      case "images":
        return "bg-purple-100 text-purple-600";
      case "music":
        return "bg-blue-100 text-blue-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getFilteredFiles = () => {
    if (selectedCategory === "all") return files;
    return files.filter((file) => {
      if (selectedCategory === "documents")
        return (
          file.file_type.includes("text") ||
          file.file_type.includes("pdf") ||
          file.file_type.includes("document")
        );
      if (selectedCategory === "videos")
        return file.file_type.startsWith("video");
      if (selectedCategory === "images")
        return file.file_type.startsWith("image");
      if (selectedCategory === "music")
        return file.file_type.startsWith("audio");
      return false;
    });
  };

  const getFilesByCategory = (category) => {
    return files.filter((file) => {
      if (category === "documents")
        return (
          file.file_type.includes("text") ||
          file.file_type.includes("pdf") ||
          file.file_type.includes("document")
        );
      if (category === "videos") return file.file_type.startsWith("video");
      if (category === "images") return file.file_type.startsWith("image");
      if (category === "music") return file.file_type.startsWith("audio");
      return false;
    });
  };

  const getTotalSize = (category = null) => {
    const categoryFiles = category ? getFilesByCategory(category) : files;
    return categoryFiles.reduce((total, file) => total + file.file_size, 0);
  };

  const categories = [
    {
      id: "documents",
      name: "Doc",
      count: getFilesByCategory("documents").length,
      size: getTotalSize("documents"),
    },
    {
      id: "videos",
      name: "Video",
      count: getFilesByCategory("videos").length,
      size: getTotalSize("videos"),
    },
    {
      id: "images",
      name: "Foto",
      count: getFilesByCategory("images").length,
      size: getTotalSize("images"),
    },
    {
      id: "music",
      name: "Music",
      count: getFilesByCategory("music").length,
      size: getTotalSize("music"),
    },
  ];

  function PreviewBody({ file }) {
    if (!file) return null;
    const src = file.direct_url;
    const type = file.file_type;

    if (type.startsWith("image/")) {
      return (
        <img
          src={src}
          alt={file.title}
          className="max-h-[70vh] w-auto mx-auto rounded-md border border-gray-700"
        />
      );
    }
    if (type.startsWith("video/")) {
      return (
        <video
          src={src}
          controls
          preload="metadata"
          className="w-full max-h-[70vh] rounded-md border border-gray-700"
        />
      );
    }
    if (type.startsWith("audio/")) {
      return (
        <div className="p-4">
          <audio src={src} controls preload="metadata" className="w-full" />
        </div>
      );
    }
    if (type === "application/pdf") {
      return (
        <iframe
          src={`${src}#toolbar=1&view=fitH`}
          className="w-full h-[75vh] rounded-md border border-gray-700"
          title={file.title}
        />
      );
    }
    if (type.startsWith("text/")) {
      return (
        <iframe
          src={src}
          className="w-full h-[75vh] rounded-md border border-gray-700"
          title={file.title}
        />
      );
    }

    return (
      <div className="text-center text-sm text-gray-300 p-6">
        Preview belum didukung untuk tipe{" "}
        <span className="font-mono">{type || "unknown"}</span>.
        <div className="mt-3">
          <Button onClick={() => window.open(file.download_url, "_blank")}>
            Download
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Toaster position="top-right" />

      {/* PWA Install Prompt */}
      {showInstallPrompt && (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-3 z-50 shadow-2xl border-b border-cyan-400/30">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-900/80 rounded p-1 border border-cyan-400/40">
                <FileText className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Install JH Drive</p>
                <p className="text-xs opacity-90">
                  Access faster, work offline
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20 text-xs px-2 border border-white/20"
                onClick={handleInstallApp}
              >
                Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20 p-1"
                onClick={dismissInstallPrompt}
              >
                ✕
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header
        className={`bg-gray-800/90 backdrop-blur-md shadow-xl border-b border-gray-700/50 sticky z-40 ${
          showInstallPrompt ? "top-16" : "top-0"
        }`}
      >
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              {/*<h1 className="text-lg font-semibold text-gray-300">Hello,</h1>*/}
              <p className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                JH Drive
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {currentUser ? (
                <Button
                  onClick={() => setShowProfile(true)}
                  variant="ghost"
                  size="sm"
                  data-testid="profile-btn"
                  className="text-gray-300 hover:bg-gray-700/50 border border-gray-600/50 hover:border-cyan-400/50"
                >
                  <User className="h-4 w-4" />
                </Button>
              ) : (
                <Dialog open={showLogin} onOpenChange={setShowLogin}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="login-btn"
                      size="sm"
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white border border-cyan-400/30 shadow-lg shadow-cyan-500/25"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Login
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md bg-gray-800/95 backdrop-blur-md border-gray-700/50">
                    <DialogHeader>
                      <DialogTitle>User Login</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="username">Username</Label>
                        <Input
                          id="username"
                          data-testid="username-input"
                          value={loginData.username}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              username: e.target.value,
                            })
                          }
                          placeholder="Enter username"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="password">Password</Label>
                        <Input
                          id="password"
                          data-testid="password-input"
                          type="password"
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              password: e.target.value,
                            })
                          }
                          placeholder="Enter password"
                          required
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowLogin(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          data-testid="login-submit-btn"
                          disabled={loading}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {loading ? "Logging in..." : "Login"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Storage Overview */}
        <Card className="relative bg-gradient-to-br from-slate-800/80 to-blue-900/60 backdrop-blur-sm border-0 overflow-hidden">
          <div className="absolute inset-0 rounded-lg">
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 animate-pulse"></div>
            <div className="absolute inset-[1px] rounded-lg bg-gradient-to-br from-slate-800/90 to-blue-900/70"></div>
            <div className="neon-border-animation"></div>
          </div>
          <CardContent className="relative p-4 z-10">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-cyan-300 mb-1 flex items-center">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2 animate-pulse"></span>
                  Ringkasan
                </h3>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-300">Ruang Digunakan</span>
                  <span className="text-sm font-medium text-cyan-400">
                    {formatFileSize(getTotalSize())}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex justify-between items-center text-sm group"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full transition-all duration-300 group-hover:scale-125 group-hover:shadow-lg ${getCategoryColor(
                        category.id
                      )
                        .replace("text-", "bg-")
                        .replace("bg-red-100", "bg-red-400/80")
                        .replace("bg-yellow-100", "bg-yellow-400/80")
                        .replace("bg-purple-100", "bg-purple-400/80")
                        .replace("bg-blue-100", "bg-blue-400/80")}`}
                    ></div>
                    <span className="capitalize text-gray-300 group-hover:text-white transition-colors">
                      {category.name}
                    </span>
                  </div>
                  <span className="text-gray-400 group-hover:text-cyan-300 transition-colors">
                    {formatFileSize(category.size)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <div>
          <h3 className="font-semibold text-gray-200 mb-4">Categories</h3>
          <div className="flex space-x-4 overflow-x-auto pb-2">
            <div
              className={`flex-shrink-0 p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                selectedCategory === "all"
                  ? "bg-gradient-to-br from-cyan-600/20 to-blue-600/20 border-cyan-400/50 shadow-lg shadow-cyan-500/25"
                  : "bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50 hover:border-gray-600/50"
              } border backdrop-blur-sm`}
              onClick={() => setSelectedCategory("all")}
            >
              <div className="flex flex-col items-center space-y-2">
                <div
                  className={`p-3 rounded-lg transition-all duration-300 ${
                    selectedCategory === "all"
                      ? "bg-cyan-500/20 text-cyan-300"
                      : "bg-gray-700/50 text-gray-400"
                  }`}
                >
                  <Folder className="h-6 w-6" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-200">All Files</p>
                  <p className="text-xs text-gray-400">{files.length} files</p>
                </div>
              </div>
            </div>

            {categories.map((category) => (
              <div
                key={category.id}
                className={`flex-shrink-0 p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                  selectedCategory === category.id
                    ? "bg-gradient-to-br from-cyan-600/20 to-blue-600/20 border-cyan-400/50 shadow-lg shadow-cyan-500/25"
                    : "bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50 hover:border-gray-600/50"
                } border backdrop-blur-sm`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <div className="flex flex-col items-center space-y-2">
                  <div
                    className={`p-3 rounded-lg transition-all duration-300 ${
                      selectedCategory === category.id
                        ? getCategoryColor(category.id)
                            .replace("bg-red-100", "bg-red-500/20")
                            .replace("bg-yellow-100", "bg-yellow-500/20")
                            .replace("bg-purple-100", "bg-purple-500/20")
                            .replace("bg-blue-100", "bg-blue-500/20")
                            .replace("text-red-600", "text-red-400")
                            .replace("text-yellow-600", "text-yellow-400")
                            .replace("text-purple-600", "text-purple-400")
                            .replace("text-blue-600", "text-blue-400")
                        : "bg-gray-700/50 text-gray-400"
                    }`}
                  >
                    {getCategoryIcon(category.id)}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-200 capitalize">
                      {category.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(category.size)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Files */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-200">File Terbaru</h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
            >
              Lihat Semua
            </Button>
          </div>

          {getFilteredFiles().length === 0 ? (
            <div className="text-center py-8" data-testid="no-files-message">
              <FileText className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-300 mb-2">
                Belum ada filenya
              </h3>
              <p className="text-gray-400">Silahkan upload dulu ya</p>
            </div>
          ) : (
            <div className="space-y-3">
              {getFilteredFiles()
                .slice(0, 10)
                .map((file) => {
                  const t = file?.file_type || "";
                  const isPreviewable =
                    t.startsWith("image/") ||
                    t.startsWith("video/") ||
                    t.startsWith("audio/") ||
                    t === "application/pdf" ||
                    t.startsWith("text/");
                  const PreviewIcon =
                    t.startsWith("audio/") || t.startsWith("video/")
                      ? PlayCircle
                      : Eye;

                  return (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-gray-800/40 backdrop-blur-sm rounded-lg border border-gray-700/50 hover:bg-gray-700/40 hover:border-gray-600/50 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-3">
                        {/* ========== Thumbnail/Icon (klik untuk preview) ========== */}
                        <button
                          type="button"
                          onClick={() => isPreviewable && openPreview(file)}
                          className="relative flex-shrink-0 group/tn"
                          aria-label="Preview"
                        >
                          {file.thumbnail_url ? (
                            <img
                              src={file.thumbnail_url}
                              alt={file.title}
                              className="w-10 h-10 object-cover rounded-lg border border-gray-600/50 group-hover/tn:border-cyan-400/50 transition-all"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.nextElementSibling.style.display =
                                  "flex";
                              }}
                            />
                          ) : null}

                          <div
                            className={`w-10 h-10 p-2 rounded-lg items-center justify-center border transition-all ${
                              t.startsWith("image/")
                                ? "bg-purple-500/20 text-purple-400 border-purple-500/30 group-hover/tn:border-purple-400/50"
                                : t.startsWith("video/")
                                ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30 group-hover/tn:border-yellow-400/50"
                                : t.startsWith("audio/")
                                ? "bg-blue-500/20 text-blue-400 border-blue-500/30 group-hover/tn:border-blue-400/50"
                                : "bg-red-500/20 text-red-400 border-red-500/30 group-hover/tn:border-red-400/50"
                            }`}
                            style={{
                              display: file.thumbnail_url ? "none" : "flex",
                            }}
                          >
                            {getFileIcon(t)}
                          </div>

                          {isPreviewable && (
                            <span className="absolute -bottom-1 -right-1 rounded-full bg-black/60 p-0.5">
                              <PreviewIcon className="h-4 w-4 text-cyan-300" />
                            </span>
                          )}
                        </button>

                        {/* ========== JUDUL & SIZE (ini yang hilang) ========== */}
                        <div className="flex-1 min-w-0">
                          {editingFile === file.id ? (
                            <div className="flex items-center space-x-2">
                              <Input
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="text-sm bg-gray-700/50 border-gray-600/50 text-gray-200"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleEditTitle(file.id)}
                                className="bg-cyan-600 hover:bg-cyan-500"
                              >
                                Save
                              </Button>
                            </div>
                          ) : (
                            <>
                              <p className="font-medium text-gray-200 text-sm truncate group-hover:text-white transition-colors">
                                {file.title || file.original_name}
                              </p>
                              <p className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                                {formatFileSize(file.file_size)}
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Menu */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-40 p-2 bg-gray-800/95 backdrop-blur-md border-gray-700/50 shadow-2xl">
                          <div className="space-y-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-left text-gray-300 hover:text-white hover:bg-gray-700/50"
                              onClick={() =>
                                window.open(file.download_url, "_blank")
                              }
                            >
                              <Download className="h-4 w-4 mr-2 text-green-400" />
                              Download
                            </Button>
                            {/* ⬇️ Copy Link — SELALU tampil */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-left text-gray-300 hover:text-white hover:bg-gray-700/50"
                              onClick={() =>
                                copyLink(file.direct_url || file.download_url)
                              }
                              data-testid={`copy-link-btn-${file.id}`}
                            >
                              <Copy className="h-4 w-4 mr-2 text-cyan-400" />
                              Copy Link
                            </Button>

                            {/* Pakai isPreviewable & PreviewIcon yang barusan didefinisikan */}
                            {isPreviewable && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-left text-gray-300 hover:text-white hover:bg-gray-700/50"
                                onClick={() => openPreview(file)}
                              >
                                <PreviewIcon className="h-4 w-4 mr-2 text-cyan-400" />
                                Preview
                              </Button>
                            )}

                            {/* Edit (ADMIN only) */}
                            {currentUser?.role === "admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-left text-gray-300 hover:text-white hover:bg-gray-700/50"
                                onClick={() => {
                                  setEditingFile(file.id);
                                  setEditTitle(file.title);
                                }}
                              >
                                <Edit3 className="h-4 w-4 mr-2 text-yellow-400" />
                                Edit
                              </Button>
                            )}

                            {/* Hapus (ADMIN only) */}
                            {currentUser?.role === "admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleDelete(file.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Hapus
                              </Button>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </main>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl bg-gray-900/95 backdrop-blur-md border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-200">
              {previewFile?.title || "Preview"}
            </DialogTitle>
          </DialogHeader>

          {previewFile ? <PreviewBody file={previewFile} /> : null}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => window.open(previewFile?.download_url, "_blank")}
            >
              Download
            </Button>
            <Button onClick={closePreview}>Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="sm:max-w-md bg-gray-800/95 backdrop-blur-md border-gray-700/50">
          <DialogHeader>
            <DialogTitle className="text-gray-200">Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {currentUser && (
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-400/30">
                  <User className="h-10 w-10 text-cyan-400" />
                </div>
                <h3 className="font-semibold text-lg text-gray-200">
                  {currentUser.username}
                </h3>
                <Badge
                  variant={
                    currentUser.role === "admin" ? "destructive" : "secondary"
                  }
                  className={
                    currentUser.role === "admin"
                      ? "mt-2 bg-red-500/20 text-red-400 border-red-500/30"
                      : "mt-2 bg-blue-500/20 text-blue-400 border-blue-500/30"
                  }
                >
                  {currentUser.role.toUpperCase()}
                </Badge>
              </div>
            )}

            <div className="space-y-2">
              {currentUser && currentUser.role === "admin" && (
                <Button
                  variant="outline"
                  className="w-full bg-gray-700/50 border-gray-600/50 text-gray-200 hover:bg-gray-600/50 hover:border-cyan-400/50"
                  onClick={() => {
                    setShowProfile(false);
                    setShowUserManagement(true);
                    fetchUsers();
                  }}
                >
                  Manage Users
                </Button>
              )}
              <Button
                variant="destructive"
                className="w-full bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Management Modal */}
      <Dialog open={showUserManagement} onOpenChange={setShowUserManagement}>
        <DialogContent className="sm:max-w-lg bg-gray-800/95 backdrop-blur-md border-gray-700/50">
          <DialogHeader>
            <DialogTitle>User Management</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Create User Form */}
            <div>
              <h4 className="font-medium mb-3">Create New User</h4>
              <form onSubmit={createUser} className="space-y-3">
                <Input
                  placeholder="Username"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  required
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  required
                />
                <div className="flex space-x-2">
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                    className="flex-1 px-3 py-2 bg-gray-800/70 border-gray-700 text-gray-100 placeholder:text-gray-400
             focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Button type="submit" disabled={loading}>
                    {loading ? "Creating..." : "Create"}
                  </Button>
                </div>
              </form>
            </div>

            <Separator />

            {/* Users List */}
            <div>
              <h4 className="font-medium mb-3">Existing Users</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-sm">{user.username}</p>
                      <p
                        className="text-xs bg-gray-800/70 border-gray-700 text-gray-100 placeholder:text-gray-400
             focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none capitalize"
                      >
                        {user.role}
                      </p>
                    </div>
                    {user.id !== currentUser?.id && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteUser(user.id)}
                      >
                        Hapus
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md bg-gray-800/95 backdrop-blur-md border-gray-700/50">
          <DialogHeader>
            <DialogTitle className="text-gray-200">Upload Baru</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-gray-300">
                Title
              </Label>
              <Input
                id="title"
                data-testid="file-title-input"
                value={uploadData.title}
                onChange={(e) =>
                  setUploadData({ ...uploadData, title: e.target.value })
                }
                placeholder="Enter file title"
                className="bg-gray-700/50 border-gray-600/50 text-gray-200 placeholder-gray-400"
                required
              />
            </div>
            <div>
              <Label htmlFor="file" className="text-gray-300">
                File
              </Label>
              <Input
                key={uploadFileInputKey}
                id="file"
                data-testid="file-upload-input"
                type="file"
                onChange={(e) =>
                  setUploadData({ ...uploadData, file: e.target.files[0] })
                }
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                className="w-full bg-red-500/20 border-red-600/30 text-red-400 hover:bg-red-500/30 file:text-white-300 file:border-0"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUpload(false)}
                className="bg-gray-700/50 border-gray-600/50 text-gray-300 hover:bg-gray-600/50"
              >
                Batal
              </Button>
              <Button
                type="submit"
                data-testid="upload-submit-btn"
                disabled={loading}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 shadow-lg shadow-cyan-500/25"
              >
                {loading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation (fixed) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-800/95 backdrop-blur-md border-t border-gray-700/50">
        <div className="relative max-w-md mx-auto">
          {/* Bar ikon kiri/kanan */}
          <div className="flex justify-between px-10 py-3">
            <Button
              variant="ghost"
              className="flex flex-col items-center space-y-1 py-2 text-gray-400 hover:text-gray-200"
            >
              <Home className="h-10 w-10" />
            </Button>

            <Button
              variant="ghost"
              className="flex flex-col items-center space-y-1 py-2 text-gray-400 hover:text-gray-200"
              onClick={() =>
                currentUser ? setShowProfile(true) : setShowLogin(true)
              }
              data-testid="profile-nav-btn"
            >
              <User className="h-10 w-10" />
            </Button>
          </div>

          {/* FAB Upload – di tengah, nyundul garis navbar */}
          {currentUser && (
            <button
              onClick={() => setShowUpload(true)}
              data-testid="upload-nav-btn"
              aria-label="Upload file"
              className="absolute left-1/2 -translate-x-1/2 -top-7 z-40
                   w-16 h-16 rounded-full
                   bg-gradient-to-r from-cyan-500 to-blue-500
                   flex items-center justify-center
                   shadow-[0_10px_25px_rgba(34,211,238,.35)]
                   ring-4 ring-gray-900 border border-white/10
                   transition-transform duration-150 hover:scale-105 active:scale-95"
            >
              <span className="absolute inset-0 rounded-full bg-white/15 blur-xl pointer-events-none" />
              <Plus className="h-7 w-7 text-white drop-shadow" />
            </button>
          )}
        </div>
      </div>

      {/* Spacer supaya konten tidak ketutup navbar/FAB */}
      <div className="h-20" />
    </div>
  );
}

export default App;
